import React, { useMemo, useContext, createContext, useCallback } from 'react';
import dailyReportsReducer, { DailyReportsState, initialDailyReportsState } from './reducer';
import { DAILY_REPORTS_FAILED, DAILY_REPORTS_REQUESTED, DAILY_REPORTS_SUCCESS } from './constants';
import { reportClient } from 'clients';
import useReducerWithMiddleware from '../useReducerWithMiddleware';
import unauthorized from '../unauthorized';
import { MonthYear } from 'utils/monthYear';
import { RentalTypeFilterValue } from 'utils/constants';

type DailyReportsFilters = {
    monthYear: MonthYear['value'];
    rentalType?: RentalTypeFilterValue;
};

interface DailyReportsActions {
    getDailyReports: (filters: DailyReportsFilters, spotPk: number) => void;
}

interface DailyReportsContextType extends DailyReportsState, DailyReportsActions {}

export const DailyReportsContext = createContext<DailyReportsContextType | null>(null);

export const DailyReportsProvider: React.FunctionComponent<React.PropsWithChildren<object>> = ({ children }) => {
    const [state, dispatch] = useReducerWithMiddleware(
        dailyReportsReducer,
        {
            ...initialDailyReportsState,
        },
        [],
        [unauthorized]
    );

    const getDailyReports = useCallback(
        async (filters: DailyReportsFilters, spotPk: number) => {
            dispatch({ type: DAILY_REPORTS_REQUESTED });
            try {
                let searchQuery = '';
                const [month, year] = filters.monthYear.split(':');
                if (month && year) searchQuery += `&month=${month}&year=${year}`;
                if (filters.rentalType) searchQuery += `&rental_type=${filters.rentalType}`;

                const reports = await reportClient.getDailyReports(spotPk, searchQuery);

                dispatch({
                    type: DAILY_REPORTS_SUCCESS,
                    payload: {
                        reports,
                    },
                });
            } catch (error) {
                dispatch({
                    type: DAILY_REPORTS_FAILED,
                    payload: {
                        error,
                        message: error?.toString() || 'Could not fetch reports',
                    },
                });
            }
        },
        // until this is fixed: https://github.com/reactjs/react.dev/issues/1889,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const value = useMemo<DailyReportsContextType>(() => {
        return {
            ...state,
            getDailyReports,
        };
    }, [state, getDailyReports]);

    return <DailyReportsContext.Provider value={value}>{children}</DailyReportsContext.Provider>;
};

export const useDailyReports = () => {
    const context = useContext(DailyReportsContext);

    if (!context) {
        throw new Error('Error: useDailyReports should be wrapped by DailyReportsProvider.');
    }

    return context;
};
